import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    IconButton,
} from '@mui/material';
import { Row, Col, Label, FormGroup, Form } from 'reactstrap';
import CustomSelect from '../../../CommonElements/CustomSelect';
import { useForm, useController } from 'react-hook-form';
import { showAlert, getCarTypes, getCorporateClientsBySeviceCityIdWithoutPagination, getPackagesByServiceCity } from '../../../Services/Shared.service';
import {
    createPayoutForPackageBulk,
    updatePackagePayoutMapping,
    getPackageDriverPayoutByPackageId,
    getPackageDriverPayoutByCorporateClientId,
} from '../../../Services/Payout.service';
import { useDispatch, useSelector } from 'react-redux';
import { getPackage } from '../../../Services/Packages.service';
import { fetchDriverPayouts } from '../../../redux/slices/tariffSlice';
import SuccessFeedbackDialog from '../../Payout/Components/SuccessFeedbackDialog';
import { RIDE_TYPE_OPTIONS, getRideTypeLabel } from '../../../Constant/serviceTypeConstants';
import PayoutSelectionTable from '../Components/PayoutSelectionTable';
import SummaryTable from '../Components/SummaryTable';
import usePayoutFiltering from '../hooks/usePayoutFiltering';

const EMPTY_FORM = { carTypeId: '', driverPayoutId: '', percentage: '' };

// ─── Main component ─────────────────────────────────────────────────────────

/**
 * CreateEditPayoutMappingForm
 *
 * Unified form for creating and editing package ↔ driver-payout mappings.
 *
 * Props:
 *  packId              — package ID (create from client context); omit when creating from payout flow
 *  corporateClientId   — corp client ID (create from client context); omit when creating from payout flow
 *  serviceCityId       — drives car type fetch
 *  existingPayoutIds   — payout IDs already mapped (excluded from dropdown in create mode)
 *  existingMappings    — full mapping rows (for time-overlap exclusion)
 *  editData            — full mapping row when editing; null/undefined = create mode
 *  onSuccess           — called after successful save/update
 *  onCancel            — called on cancel
 */
const CreateEditPayoutMappingForm = ({
    packId,
    corporateClientId: corporateClientIdProp,
    serviceCityId,
    existingPayoutIds = [],
    existingMappings = [],
    editData,
    onSuccess,
    onCancel,
}) => {
    const isEditMode = Boolean(editData);

    const dispatch = useDispatch();
    const { driverPayouts: allDriverPayouts, loading: loadingPayouts } = useSelector((state) => state.tariff);

    // ── Car types ────────────────────────────────────────────────────────────
    const [carTypes, setCarTypes] = useState([]);
    const [loadingCarTypes, setLoadingCarTypes] = useState(false);

    // ── Fetch package rideType for packId ────────────────────────────────────
    const [packRideType, setPackRideType] = useState(null);

    useEffect(() => {
        if (packId) {
            getPackage({ packageId: packId })
                .then(resp => {
                    const rideType = resp?.data?.packageRideType;
                    if (rideType) setPackRideType(rideType);
                })
                .catch(console.error);
        }
    }, [packId]);

    // Effective serviceCityId: prop or derive from editData payout
    const effectiveServiceCityId = serviceCityId || editData?.driverPayout?.serviceCityId;

    useEffect(() => {
        if (!effectiveServiceCityId) { setCarTypes([]); return; }
        setLoadingCarTypes(true);
        getCarTypes({ serviceCityId: effectiveServiceCityId })
            .then((resp) => {
                const cars = resp?.data?.list || resp?.data || [];
                setCarTypes(cars.map((c) => ({ value: c.id, label: c.name })));
            })
            .catch(() => setCarTypes([]))
            .finally(() => setLoadingCarTypes(false));
    }, [effectiveServiceCityId]);

    // ── Driver payouts (Redux cache) ─────────────────────────────────────────
    useEffect(() => {
        dispatch(fetchDriverPayouts());
    }, [dispatch]);

    // ── Corporate client / Packages toggle (create from payout flow only) ─────
    const showCorpClientDropdown = !isEditMode && !packId && !corporateClientIdProp;
    const [mappingType, setMappingType] = useState('cc'); // 'cc' = Corporate Client, 'p' = Packages
    const [corporateClients, setCorporateClients] = useState([]);
    const [loadingClients, setLoadingClients] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);

    // Ride Type state — shown when CC mode is active (toggle or prop)
    const showRideTypeDropdown = !isEditMode && (showCorpClientDropdown ? mappingType === 'cc' : Boolean(corporateClientIdProp));
    const [selectedRideType, setSelectedRideType] = useState(null);

    // Packages state
    const [packagesList, setPackagesList] = useState([]);
    const [loadingPackages, setLoadingPackages] = useState(false);
    const [selectedPackage, setSelectedPackage] = useState(null);

    useEffect(() => {
        if (!showCorpClientDropdown || !serviceCityId) return;
        // Fetch corporate clients
        setLoadingClients(true);
        getCorporateClientsBySeviceCityIdWithoutPagination({ serviceCityId })
            .then((resp) => {
                const list = resp?.data?.list || resp?.data || [];
                setCorporateClients(list.map((c) => ({ value: c.id, label: c.companyName })));
            })
            .catch(() => setCorporateClients([]))
            .finally(() => setLoadingClients(false));
        // Fetch packages
        setLoadingPackages(true);
        getPackagesByServiceCity({ serviceCityId })
            .then((resp) => {
                const list = resp?.data?.list || resp?.data || [];
                setPackagesList(list.map((p) => ({ value: p.id, label: p.packageName || p.name })));
            })
            .catch(() => setPackagesList([]))
            .finally(() => setLoadingPackages(false));
    }, [showCorpClientDropdown, serviceCityId]);

    // ── Fetch sibling mappings for create-from-payout-flow corp client / package ─
    const [fetchedExistingMappings, setFetchedExistingMappings] = useState([]);
    useEffect(() => {
        if (!showCorpClientDropdown) {
            setFetchedExistingMappings([]);
            return;
        }
        if (mappingType === 'cc' && selectedClient?.value) {
            getPackageDriverPayoutByCorporateClientId(selectedClient.value)
                .then((resp) => setFetchedExistingMappings(resp?.data?.list || resp?.data || []))
                .catch(() => setFetchedExistingMappings([]));
        } else if (mappingType === 'p' && selectedPackage?.value) {
            getPackageDriverPayoutByPackageId(selectedPackage.value)
                .then((resp) => setFetchedExistingMappings(resp?.data?.list || resp?.data || []))
                .catch(() => setFetchedExistingMappings([]));
        } else {
            setFetchedExistingMappings([]);
        }
    }, [showCorpClientDropdown, mappingType, selectedClient, selectedPackage]);

    // Resolved targets
    const resolvedCorporateClientId = corporateClientIdProp || (mappingType === 'cc' ? selectedClient?.value : null) || null;
    const resolvedPackagesId = packId || (mappingType === 'p' ? selectedPackage?.value : null) || null;

    // Effective existing mappings (prop takes priority)
    const resolvedExistingMappings = existingMappings.length > 0 ? existingMappings : fetchedExistingMappings;
    const resolvedExistingPayoutIds =
        existingPayoutIds.length > 0
            ? existingPayoutIds
            : fetchedExistingMappings.map((r) => r.driverPayout?.id).filter(Boolean);

    // ── Edit mode: fetch sibling mappings if not provided by parent ───────────
    const [siblingMappings, setSiblingMappings] = useState([]);
    useEffect(() => {
        if (!isEditMode || existingMappings.length > 0) return;
        const fetchFn = editData?.corporateClientId
            ? getPackageDriverPayoutByCorporateClientId(editData.corporateClientId)
            : editData?.packagesId
            ? getPackageDriverPayoutByPackageId(editData.packagesId)
            : null;
        if (!fetchFn) return;
        fetchFn
            .then((resp) => setSiblingMappings(resp?.data?.list || resp?.data || []))
            .catch(() => setSiblingMappings([]));
    }, [isEditMode, editData, existingMappings.length]);

    const effectiveSiblings = existingMappings.length > 0 ? existingMappings : siblingMappings;

    const [clientError, setClientError] = useState('');
    const [entries, setEntries] = useState([]);
    const [editingIndex, setEditingIndex] = useState(null);

    // State for create-mode payout table selection
    const [selectedPayoutId, setSelectedPayoutId] = useState(null);
    const [rowPercentages, setRowPercentages] = useState({});

    const { register, control, handleSubmit, reset, watch, setValue, formState: { errors: formErrors } } = useForm({
        defaultValues: EMPTY_FORM,
        mode: 'onChange'
    });

    useEffect(() => {
        if (!isEditMode || !editData) return;
        const payout = editData.driverPayout || {};
        reset({
            carTypeId: payout.carTypeId || '',
            driverPayoutId: editData.driverPayoutId || '',
            percentage:
                editData.percentage !== null && editData.percentage !== undefined
                    ? String(editData.percentage)
                    : '',
        });
    }, [isEditMode, editData, reset]);

    const watchCarTypeId = watch("carTypeId");
    const watchDriverPayoutId = watch("driverPayoutId");

    const carTypeIdField = useController({
        name: 'carTypeId',
        control,
        rules: { required: 'Car Type is required' }
    }).field;

    const driverPayoutIdField = useController({
        name: 'driverPayoutId',
        control,
        rules: { required: 'Driver Payout is required' }
    }).field;

    const { computeFilteredPayouts, filteredPayouts, payoutFeedback } = usePayoutFiltering({
        allDriverPayouts,
        effectiveServiceCityId,
        resolvedExistingPayoutIds,
        resolvedExistingMappings,
        entries,
        isEditMode,
        editData,
        showCorpClientDropdown,
        mappingType,
        selectedClient,
        selectedPackage,
        showRideTypeDropdown,
        selectedRideType,
        editingIndex,
        watchCarTypeId,
        watchDriverPayoutId,
        effectiveSiblings,
    });



    // Select a row in the payout table — clears the previously selected row's percentage
    const handleSelectPayout = (payoutId) => {
        setSelectedPayoutId((prev) => {
            if (prev !== null && prev !== payoutId) {
                setRowPercentages((p) => { const n = { ...p }; delete n[prev]; return n; });
            }
            return payoutId;
        });
    };

    // Add from the payout selection table (create mode)
    const handleTableAdd = (payoutId, percentage) => {
        if (showCorpClientDropdown) {
            if (mappingType === 'cc' && !selectedClient) {
                setClientError('Corporate Client is required');
                return;
            }
            if (mappingType === 'p' && !selectedPackage) {
                setClientError('Package is required');
                return;
            }
        }
        if (showRideTypeDropdown && !selectedRideType) {
            setClientError('Ride Type is required');
            return;
        }
        const snapshot = showCorpClientDropdown
            ? mappingType === 'cc' && selectedClient
                ? { corporateClientId: selectedClient.value, corporateClientLabel: selectedClient.label }
                : mappingType === 'p' && selectedPackage
                ? { packagesId: selectedPackage.value, packagesLabel: selectedPackage.label }
                : {}
            : {};
        const rideTypeSnapshot = showRideTypeDropdown && selectedRideType
            ? { rideType: selectedRideType.value, rideTypeLabel: selectedRideType.label }
            : {};
        setEntries((prev) => [
            ...prev,
            { carTypeId: watchCarTypeId, driverPayoutId: payoutId, percentage, ...snapshot, ...rideTypeSnapshot },
        ]);
        // Clear the percentage for that row after adding
        setRowPercentages((prev) => { const next = { ...prev }; delete next[payoutId]; return next; });
        setSelectedPayoutId(null);
    };

    const handleAddEntry = (data) => {
        if (showCorpClientDropdown) {
            if (mappingType === 'cc' && !selectedClient) {
                setClientError('Corporate Client is required');
                return;
            }
            if (mappingType === 'p' && !selectedPackage) {
                setClientError('Package is required');
                return;
            }
        }
        if (showRideTypeDropdown && !selectedRideType) {
            setClientError('Ride Type is required');
            return;
        }

        const snapshot = showCorpClientDropdown
            ? mappingType === 'cc' && selectedClient
                ? { corporateClientId: selectedClient.value, corporateClientLabel: selectedClient.label }
                : mappingType === 'p' && selectedPackage
                ? { packagesId: selectedPackage.value, packagesLabel: selectedPackage.label }
                : {}
            : {};
        const rideTypeSnapshot = showRideTypeDropdown && selectedRideType
            ? { rideType: selectedRideType.value, rideTypeLabel: selectedRideType.label }
            : {};

        if (editingIndex !== null) {
            const newEntries = [...entries];
            newEntries[editingIndex] = { ...data, ...snapshot, ...rideTypeSnapshot };
            setEntries(newEntries);
            setEditingIndex(null);
        } else {
            setEntries((prev) => [...prev, { ...data, ...snapshot, ...rideTypeSnapshot }]);
        }
        reset(EMPTY_FORM);
    };

    const handleEditEntry = (index) => {
        const entry = entries[index];
        reset({ ...entry });
        setEditingIndex(index);
        if (showCorpClientDropdown && entry.corporateClientId) {
            setMappingType('cc');
            setSelectedClient({ value: entry.corporateClientId, label: entry.corporateClientLabel });
            if (clientError) setClientError('');
        } else if (showCorpClientDropdown && entry.packagesId) {
            setMappingType('p');
            setSelectedPackage({ value: entry.packagesId, label: entry.packagesLabel });
            if (clientError) setClientError('');
        }
        // Restore ride type
        if (entry.rideType) {
            setSelectedRideType({ value: entry.rideType, label: entry.rideTypeLabel });
        } else {
            setSelectedRideType(null);
        }
    };

    const handleRemoveEntry = (index) => {
        setEntries((prev) => prev.filter((_, i) => i !== index));
        if (editingIndex === index) {
            setEditingIndex(null);
            reset(EMPTY_FORM);
        } else if (editingIndex !== null && editingIndex > index) {
            setEditingIndex((prev) => prev - 1);
        }
    };

    const handleCancelEdit = () => {
        setEditingIndex(null);
        reset(EMPTY_FORM);
    };

    // ── Saving ────────────────────────────────────────────────────────────────

    const [saving, setSaving] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);

    const handleSave = async () => {
        if (saving) return;
        if (showCorpClientDropdown) {
            if (mappingType === 'cc' && !selectedClient) {
                setClientError('Corporate Client is required');
                return;
            }
            if (mappingType === 'p' && !selectedPackage) {
                setClientError('Package is required');
                return;
            }
        }
        if (showRideTypeDropdown && !selectedRideType) {
            setClientError('Ride Type is required');
            return;
        }

        setSaving(true);
        try {
            const mappings = entries.map((entry) => ({
                driverPayoutId: Number(entry.driverPayoutId),
                // Use per-entry snapshotted values when dropdown was active,
                // otherwise fall back to the prop-supplied ids.
                ...(entry.corporateClientId
                    ? { corporateClientId: Number(entry.corporateClientId) }
                    : entry.packagesId
                    ? { packagesId: Number(entry.packagesId) }
                    : resolvedCorporateClientId
                    ? { corporateClientId: Number(resolvedCorporateClientId) }
                    : resolvedPackagesId
                    ? { packagesId: Number(resolvedPackagesId) }
                    : { packagesId: Number(packId) }),
                percentage: parseFloat(entry.percentage),
                ...(entry.rideType ? { rideType: entry.rideType } : packRideType ? { rideType: packRideType } : {}),
            }));
            await createPayoutForPackageBulk(mappings);
            setShowSuccessDialog(true);
        } catch (error) {
            console.error('Mapping submission error:', error);
            showAlert('error', error.response?.data?.message || 'Failed to create payout mappings');
        } finally {
            setSaving(false);
        }
    };

    const handleUpdate = async (data) => {
        if (saving) return;
        setSaving(true);
        try {
            const payload = {
                ...(editData.corporateClientId
                    ? { corporateClientId: editData.corporateClientId }
                    : { packagesId: editData.packagesId }),
                driverPayoutId: Number(data.driverPayoutId),
                percentage: parseFloat(data.percentage),
                ...(editData?.rideType ? { rideType: editData.rideType } : {}),
            };
            await updatePackagePayoutMapping(editData.id, payload);
            showAlert('success', 'Mapping updated successfully');
            if (onSuccess) onSuccess();
        } catch (error) {
            console.error('Error updating mapping:', error);
            showAlert('error', error.response?.data?.message || 'Failed to update mapping');
        } finally {
            setSaving(false);
        }
    };

    const handleSuccessClose = () => {
        setShowSuccessDialog(false);
        if (onSuccess) onSuccess();
    };

    // ── Derived flags ─────────────────────────────────────────────────────────

    const hasTarget = Boolean(packId) || Boolean(corporateClientIdProp) || Boolean(selectedClient) || Boolean(selectedPackage);
    const isConfigured =
        entries.length > 0 &&
        entries.every(
            (e) => e.carTypeId && e.driverPayoutId && e.percentage !== '' && e.percentage !== undefined
        );

    // Corporate client display for edit mode
    const editCorpClientLabel =
        editData?.companyName ||
        editData?.corporateClientName ||
        (editData?.corporateClientId ? `Client #${editData.corporateClientId}` : null);

    // Package display for edit mode
    const editPackageLabel =
        editData?.packageName ||
        (editData?.packagesId ? `Package #${editData.packagesId}` : null);


    const getPayoutLabel = useCallback(
        (id) => allDriverPayouts.find((p) => p.id === id)?.name || '',
        [allDriverPayouts]
    );

    const editRideTypeLabel = editData?.rideType ? getRideTypeLabel(editData.rideType) : null;
    const colSize = editCorpClientLabel || editPackageLabel || editRideTypeLabel || showCorpClientDropdown || showRideTypeDropdown ? 4 : 6;
    return (
        <Box sx={{ p: 3 }}>
            <Form onSubmit={handleSubmit(isEditMode ? handleUpdate : handleAddEntry)}>
                <Row>
                    {isEditMode && editCorpClientLabel && editRideTypeLabel && (
                        <FormGroup className={`col-lg-${colSize}`} style={{ opacity: 0.5 }}>
                            <Label className="col-lg-12 col-form-label">Corporate Client</Label>
                            <Col lg="12" style={{ pointerEvents: "none" }}>
                                <CustomSelect
                                    className="service-select"
                                    classNamePrefix="select"
                                    value={{ value: editData.corporateClientId, label: editCorpClientLabel }}
                                    isDisabled
                                    options={[]}
                                />
                            </Col>
                        </FormGroup>
                    )}
                    {isEditMode && editRideTypeLabel && (
                        <FormGroup className={`col-lg-${colSize}`} style={{ opacity: 0.5 }}>
                            <Label className="col-lg-12 col-form-label">Ride Type</Label>
                            <Col lg="12" style={{ pointerEvents: "none" }}>
                                <CustomSelect
                                    className="service-select"
                                    classNamePrefix="select"
                                    value={{ value: editData.rideType, label: editRideTypeLabel }}
                                    isDisabled
                                    options={[]}
                                />
                            </Col>
                        </FormGroup>
                    )}
                    {isEditMode && !editRideTypeLabel && editPackageLabel && (
                        <FormGroup className={`col-lg-${colSize}`} style={{ opacity: 0.5 }}>
                            <Label className="col-lg-12 col-form-label">Package</Label>
                            <Col lg="12" style={{ pointerEvents: "none" }}>
                                <CustomSelect
                                    className="service-select"
                                    classNamePrefix="select"
                                    value={{ value: editData.packagesId, label: editPackageLabel }}
                                    isDisabled
                                    options={[]}
                                />
                            </Col>
                        </FormGroup>
                    )}
                    {/* Ride Type dropdown when corporateClientId is passed as prop */}
                    {!isEditMode && !showCorpClientDropdown && showRideTypeDropdown && (
                        <FormGroup className={`col-lg-${colSize}`}>
                            <Label className="col-lg-12 col-form-label">
                                Ride Type <span className="text-danger">*</span>
                            </Label>
                            <Col lg="12">
                                <CustomSelect
                                    className="service-select"
                                    classNamePrefix="select"
                                    value={selectedRideType}
                                    onChange={(option) => {
                                        setSelectedRideType(option);
                                        if (clientError) setClientError('');
                                    }}
                                    options={RIDE_TYPE_OPTIONS}
                                    isClearable
                                    maxMenuHeight={150}
                                    placeholder="Select Ride Type"
                                    isDisabled={editingIndex !== null}
                                    isSearchable={false}
                                />
                                {clientError && clientError.includes('Ride Type') && <span style={{ color: "red" }}>{clientError}</span>}
                            </Col>
                        </FormGroup>
                    )}
                    {!isEditMode && showCorpClientDropdown && (
                        <>
                            <FormGroup className={`col-lg-${colSize}`}>
                                <Label className="col-lg-12 col-form-label">Mapping Type</Label>
                                <Col lg="12">
                                    <CustomSelect
                                        className="service-select"
                                        classNamePrefix="select"
                                        value={{ value: mappingType, label: mappingType === 'cc' ? 'Corporate Client' : 'Package' }}
                                        onChange={(option) => {
                                            if (option && option.value !== mappingType) {
                                                setMappingType(option.value);
                                                setSelectedClient(null);
                                                setSelectedPackage(null);
                                                setSelectedRideType(null);
                                                setFetchedExistingMappings([]);
                                                if (clientError) setClientError('');
                                            }
                                        }}
                                        options={[
                                            { value: 'cc', label: 'Corporate Client' },
                                            { value: 'p', label: 'Package' },
                                        ]}
                                        isDisabled={editingIndex !== null}
                                        isSearchable={false}
                                    />
                                </Col>
                            </FormGroup>

                            {mappingType === 'cc' && (
                                <FormGroup className={`col-lg-${colSize}`}>
                                    <Label className="col-lg-12 col-form-label">
                                        Corporate Client <span className="text-danger">*</span>
                                    </Label>
                                    <Col lg="12">
                                        <CustomSelect
                                            className="service-select"
                                            classNamePrefix="select"
                                            value={selectedClient}
                                            onChange={(option) => {
                                                setSelectedClient(option);
                                                if (clientError) setClientError('');
                                            }}
                                            options={corporateClients}
                                            isLoading={loadingClients}
                                            isClearable
                                            maxMenuHeight={150}
                                            placeholder={serviceCityId ? 'Select Corporate Client' : 'Select a service city first'}
                                            isDisabled={!serviceCityId || loadingClients || editingIndex !== null}
                                        />
                                        {clientError && <span style={{ color: "red" }}>{clientError}</span>}
                                    </Col>
                                </FormGroup>
                            )}

                            {mappingType === 'cc' && (
                                <FormGroup className={`col-lg-${colSize}`}>
                                    <Label className="col-lg-12 col-form-label">
                                        Ride Type <span className="text-danger">*</span>
                                    </Label>
                                    <Col lg="12">
                                        <CustomSelect
                                            className="service-select"
                                            classNamePrefix="select"
                                            value={selectedRideType}
                                            onChange={(option) => {
                                                setSelectedRideType(option);
                                                if (clientError) setClientError('');
                                            }}
                                            options={RIDE_TYPE_OPTIONS}
                                            isClearable
                                            maxMenuHeight={150}
                                            placeholder="Select Ride Type"
                                            isDisabled={editingIndex !== null}
                                            isSearchable={false}
                                        />
                                        {clientError && clientError.includes('Ride Type') && <span style={{ color: "red" }}>{clientError}</span>}
                                    </Col>
                                </FormGroup>
                            )}

                            {mappingType === 'p' && (
                                <FormGroup className={`col-lg-${colSize}`}>
                                    <Label className="col-lg-12 col-form-label">
                                        Package <span className="text-danger">*</span>
                                    </Label>
                                    <Col lg="12">
                                        <CustomSelect
                                            className="service-select"
                                            classNamePrefix="select"
                                            value={selectedPackage}
                                            onChange={(option) => {
                                                setSelectedPackage(option);
                                                if (clientError) setClientError('');
                                            }}
                                            options={packagesList}
                                            isLoading={loadingPackages}
                                            isClearable
                                            maxMenuHeight={150}
                                            placeholder={serviceCityId ? 'Select Package' : 'Select a service city first'}
                                            isDisabled={!serviceCityId || loadingPackages || editingIndex !== null}
                                        />
                                        {clientError && <span style={{ color: "red" }}>{clientError}</span>}
                                    </Col>
                                </FormGroup>
                            )}
                        </>
                    )}

                    {/* Car Type — always visible */}
                    <FormGroup className={`col-lg-${isEditMode ? colSize : (editCorpClientLabel || showCorpClientDropdown ? 4 : colSize)}`}
                        style={isEditMode ? { opacity: 0.5 } : {}}
                    >
                        <Label className="col-lg-12 col-form-label">
                            Car Type <span className="text-danger">*</span>
                        </Label>
                        <Col lg="12" style={isEditMode ? { pointerEvents: "none" } : {}}>
                            <CustomSelect
                                className="service-select"
                                classNamePrefix="select"
                                value={carTypes.find((ct) => ct.value === carTypeIdField.value) || null}
                                onChange={(selected) => {
                                    carTypeIdField.onChange(selected?.value || '');
                                    setValue('driverPayoutId', '');
                                    // Reset table selection when car type changes
                                    setSelectedPayoutId(null);
                                    setRowPercentages({});
                                }}
                                options={carTypes}
                                isLoading={loadingCarTypes}
                                isClearable={!isEditMode}
                                placeholder="Select Car Type"
                                isDisabled={isEditMode || saving || loadingPayouts || loadingCarTypes}
                            />
                            {formErrors.carTypeId && <span style={{ color: "red" }}>{formErrors.carTypeId.message}</span>}
                        </Col>
                    </FormGroup>

                    {/* Edit mode only: Driver Payout dropdown + Percentage input */}
                    {isEditMode && (
                        <>
                            <FormGroup className={`col-lg-${colSize}`} style={{ opacity: 0.5 }}>
                                <Label className="col-lg-12 col-form-label">
                                    Driver Payout <span className="text-danger">*</span>
                                </Label>
                                <Col lg="12" style={{ pointerEvents: "none" }}>
                                    <CustomSelect
                                        className="service-select"
                                        classNamePrefix="select"
                                        value={
                                            driverPayoutIdField.value
                                                ? { value: driverPayoutIdField.value, label: getPayoutLabel(driverPayoutIdField.value) }
                                                : null
                                        }
                                        onChange={(selected) => driverPayoutIdField.onChange(selected?.value || '')}
                                        options={filteredPayouts.map((p) => ({ value: p.id, label: p.name }))}
                                        isDisabled={isEditMode || !watchCarTypeId || saving || loadingPayouts}
                                        isClearable={!isEditMode}
                                        placeholder={!watchCarTypeId ? 'Select car type first' : 'Select Payout'}
                                    />
                                    {payoutFeedback && <span style={{ color: "red" }}>{payoutFeedback}</span>}
                                    {formErrors.driverPayoutId && <span style={{ color: "red" }}>{formErrors.driverPayoutId.message}</span>}
                                </Col>
                            </FormGroup>
                            <FormGroup className={`col-lg-${colSize}`}>
                                <Label className="col-lg-12 col-form-label">
                                    Deduction Percentage (%) <span className="text-danger">*</span>
                                </Label>
                                <Col lg="12">
                                    <input
                                        className="form-control"
                                        type="number"
                                        disabled={saving || loadingPayouts}
                                        {...register("percentage", {
                                            required: 'Percentage is required',
                                            min: { value: 0, message: 'Percentage cannot be negative' },
                                            max: { value: 100, message: 'Percentage cannot exceed 100' },
                                        })}
                                    />
                                    {formErrors.percentage && <span style={{ color: "red" }}>{formErrors.percentage.message}</span>}
                                </Col>
                            </FormGroup>
                        </>
                    )}
                </Row>

                {/* Create mode: payout selection table */}
                {!isEditMode && watchCarTypeId && (
                    <>
                        {editingIndex !== null ? (
                            // When editing an entry: show inline fields for that entry
                            <Row>
                                <FormGroup className="col-lg-6">
                                    <Label className="col-lg-12 col-form-label">
                                        Driver Payout <span className="text-danger">*</span>
                                    </Label>
                                    <Col lg="12">
                                        <CustomSelect
                                            className="service-select"
                                            classNamePrefix="select"
                                            value={
                                                driverPayoutIdField.value
                                                    ? { value: driverPayoutIdField.value, label: getPayoutLabel(driverPayoutIdField.value) }
                                                    : null
                                            }
                                            onChange={(selected) => driverPayoutIdField.onChange(selected?.value || '')}
                                            options={filteredPayouts.map((p) => ({ value: p.id, label: p.name }))}
                                            isDisabled={saving || loadingPayouts}
                                            isClearable
                                            placeholder="Select Payout"
                                        />
                                        {formErrors.driverPayoutId && <span style={{ color: "red" }}>{formErrors.driverPayoutId.message}</span>}
                                    </Col>
                                </FormGroup>
                                <FormGroup className="col-lg-6">
                                    <Label className="col-lg-12 col-form-label">
                                        Deduction Percentage (%) <span className="text-danger">*</span>
                                    </Label>
                                    <Col lg="12">
                                        <input
                                            className="form-control"
                                            type="number"
                                            disabled={saving || loadingPayouts}
                                            {...register("percentage", {
                                                required: 'Percentage is required',
                                                min: { value: 0, message: 'Percentage cannot be negative' },
                                                max: { value: 100, message: 'Percentage cannot exceed 100' },
                                            })}
                                        />
                                        {formErrors.percentage && <span style={{ color: "red" }}>{formErrors.percentage.message}</span>}
                                    </Col>
                                </FormGroup>
                            </Row>
                        ) : (
                            // Normal create mode: show payout table
                            <PayoutSelectionTable
                                payouts={filteredPayouts}
                                selectedPayoutId={selectedPayoutId}
                                rowPercentages={rowPercentages}
                                onSelectPayout={handleSelectPayout}
                                onPercentageChange={(id, val) => {
                                    handleSelectPayout(id);
                                    setRowPercentages((prev) => ({ ...prev, [id]: val }));
                                }}
                                onAdd={handleTableAdd}
                                editingIndex={editingIndex}
                                loading={saving || loadingPayouts}
                            />
                        )}
                    </>
                )}

                {/* Edit-entry action buttons */}
                {!isEditMode && editingIndex !== null && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
                        <button
                            type="submit"
                            className="btn"
                            disabled={loadingPayouts || saving}
                            style={{
                                backgroundColor: "rgba(57, 181, 74, 1)",
                                color: "white",
                                border: "none",
                                padding: "8px 20px",
                                borderRadius: "6px",
                                minWidth: "80px"
                            }}
                        >
                            Update Entry
                        </button>
                        <button
                            type="button"
                            className="btn"
                            onClick={handleCancelEdit}
                            disabled={saving}
                            style={{
                                backgroundColor: "white",
                                color: "#626262",
                                border: "1px solid #d9d9d9",
                                padding: "8px 20px",
                                borderRadius: "6px"
                            }}
                        >
                            Cancel
                        </button>
                    </Box>
                )}
            </Form>

            {!isEditMode && (
                <Box sx={{ mt: 3 }}>
                    <SummaryTable
                        entries={entries}
                        carTypes={carTypes}
                        allDriverPayouts={allDriverPayouts}
                        editingIndex={editingIndex}
                        onEdit={handleEditEntry}
                        onRemove={handleRemoveEntry}
                        showPartnerColumn={showCorpClientDropdown}
                        mappingType={mappingType}
                    />
                </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                <button
                    type="button"
                    className="btn"
                    onClick={onCancel}
                    disabled={saving}
                    style={{
                        backgroundColor: "white",
                        color: "#626262",
                        border: "1px solid #d9d9d9",
                        padding: "8px 20px",
                        borderRadius: "6px"
                    }}
                >
                    Cancel
                </button>

                {isEditMode ? (
                    <button
                        type="button"
                        className="btn"
                        onClick={handleSubmit(handleUpdate)}
                        disabled={saving}
                        style={{
                            backgroundColor: "rgba(57, 181, 74, 1)",
                            color: "white",
                            border: "none",
                            padding: "8px 20px",
                            borderRadius: "6px"
                        }}
                    >
                        {saving ? 'Updating...' : 'Update'}
                    </button>
                ) : (
                    <button
                        type="button"
                        className="btn"
                        onClick={handleSave}
                        disabled={saving || !isConfigured || !hasTarget}
                        style={{
                            backgroundColor: "rgba(57, 181, 74, 1)",
                            color: "white",
                            border: "none",
                            padding: "8px 20px",
                            borderRadius: "6px"
                        }}
                    >
                        {saving ? 'Saving...' : 'Save Mappings'}
                    </button>
                )}
            </Box>

            {showSuccessDialog && (
                <SuccessFeedbackDialog
                    open={showSuccessDialog}
                    onClose={handleSuccessClose}
                    message={
                        resolvedCorporateClientId
                            ? "Mappings created for specific Corporate Client."
                            : "Mappings created successfully."
                    }
                />
            )}
        </Box>
    );
};

export default CreateEditPayoutMappingForm;
